import React, { useEffect, useRef, useState } from 'react';
import { analyzeSafetyReport, transcribeAudio } from '../services/aiService';
import { RiskLevel, Category, AIAnalysisResult } from '../types';

/**
 * DemoExperience — Claude Design 由来（results/安全ポスト＋ デモ画面.dc.html）のデモ体験を React 化。
 *
 * これは「デモ」専用のスクリプト再生ツアー（本番のGemini/Supabase送信はしない）。
 * - デスクトップ(≥1024px): 左に画面ガイドのレール + 中央にスマホ枠 + 周囲ガターに番号付きコールアウト（解説付きツアー）。
 * - モバイル(<1024px): レール/コールアウトを畳んで、スマホ画面が全画面アプリになる。
 * スマホ内は QRスキャン → ホーム → 入力 →(処理)→ 確認 → 完了 を内部stateで遷移する。
 *
 * 実QRトークンでの本物の報告フローは従来どおり views/WorkerFlow.tsx を使う（App.tsx 側で分岐）。
 */

type Screen = 'qr' | 'home' | 'input' | 'confirm' | 'success';
type Method = 'text' | 'voice' | 'photo';
type Rec = 'idle' | 'rec' | 'done';

interface DemoExperienceProps {
  teamName?: string;
  onBackToLp: () => void;
  onSignup: () => void;
  onTryWhyDemo: () => void;
}

/** デザインのインラインCSS文字列を React.CSSProperties に変換（移植忠実度のため） */
const sx = (css: string): React.CSSProperties => {
  const out: Record<string, string> = {};
  css.split(';').forEach((decl) => {
    const i = decl.indexOf(':');
    if (i === -1) return;
    const rawKey = decl.slice(0, i).trim();
    const val = decl.slice(i + 1).trim();
    if (!rawKey || !val) return;
    const key = rawKey.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    out[key] = val;
  });
  return out as React.CSSProperties;
};

const DEMO_CSS = `
@keyframes apFade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@keyframes apBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-9px)}}
@keyframes apSpin{to{transform:rotate(360deg)}}
@keyframes apPing{0%{transform:scale(1);opacity:.9}80%,100%{transform:scale(2.2);opacity:0}}
@keyframes apScan{0%{top:14px}50%{top:218px}100%{top:14px}}
@keyframes apPulseRing{0%{box-shadow:0 0 0 0 rgba(249,115,22,.55)}70%{box-shadow:0 0 0 9px rgba(249,115,22,0)}100%{box-shadow:0 0 0 0 rgba(249,115,22,0)}}
@keyframes apBlink{0%,100%{opacity:1}50%{opacity:.4}}
@keyframes apNudge{0%,100%{transform:translateX(0)}50%{transform:translateX(4px)}}
@media (prefers-reduced-motion: reduce){.ap-demo *{animation:none !important;transition:none !important}}
@media (max-width:1024px){
  .ap-demo [data-stage]{background:#f8fafc !important;padding:0 !important}
  .ap-demo [data-deskonly]{display:none !important}
  .ap-demo [data-mobileonly]{display:block !important}
  /* Safari 対策: 横flex > 縦flex(align-items:center) の入れ子だと width:100% が
     max-content に縮む（Chromium は伸びるので気づけない）。実機の iOS Safari で
     ヘッダが画面幅の 39〜68% になり、日本語が「AIが内容を分/析中」のように
     語中で折り返していた。モバイルでは flex を解いて素直なブロックにする。 */
  .ap-demo [data-tour]{display:block !important}
  .ap-demo [data-phonewrap]{display:block !important;width:100% !important}
  .ap-demo [data-phone]{width:100% !important;max-width:100% !important;border-radius:0 !important;padding:0 !important;box-shadow:none !important;background:#fff !important}
  /* モバイルでは画面の高さを埋めない。
     元は min-height:calc(100vh - 52px) だったが、-52px は上のデモ帯の高さを
     52px と見積もったもので、実測は 209px ある。結果 帯(209) + カード(792) が
     可視領域(844) を超えて縦スクロールが生まれ、
       ・入力画面ではカード内に死んだ余白ができる
       ・スクロールすると上端のデモ帯が途中で切れる
     という2つの症状になっていた。高さは中身に任せる。
     カメラ画面のように高さが要る画面は各画面側で min-height を持っている。 */
  .ap-demo [data-screen-card]{border-radius:0 !important;height:auto !important;width:100% !important;min-height:0 !important}
  .ap-demo [data-stage]{min-height:100vh !important;min-height:100dvh !important}
  .ap-demo [data-backbar]{position:static !important;top:auto !important;left:auto !important;padding:12px 14px 2px !important;margin-bottom:0 !important}
}
`;

const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

const fmtTime = (n: number) => {
  const m = Math.floor(n / 60), sec = n % 60;
  return (m < 10 ? '0' + m : m) + ':' + (sec < 10 ? '0' + sec : sec);
};

const DEMO_BULLETS = [
  { n: '1', text: '現場ワーカーが使う報告画面を体験できます' },
  { n: '2', text: 'テキスト・音声・写真で報告 → AIが自動分析' },
  { n: '3', text: '実際の現場ではQRコードをスキャンして利用します' },
];

const STEPS: { key: Screen; num: string; title: string; desc: string }[] = [
  { key: 'qr', num: 'Q', title: 'QRスキャン', desc: '現場に掲示したQRをスマホで読むだけ。アプリ不要・ログイン不要。' },
  { key: 'home', num: '0', title: 'ホーム', desc: 'チームの安全状況。0件でも前向きな空状態で、最初の報告を後押し。' },
  { key: 'input', num: '1', title: '報告を入力', desc: 'テキスト・音声・写真を等価に。大きなボタン・やさしい日本語。' },
  { key: 'confirm', num: '2', title: 'AIが整形・確認', desc: '生の入力をAIが匿名化・整形し、4M分類・危険度・教訓を生成。' },
  { key: 'success', num: '3', title: '送信完了', desc: '達成感と、本番の「対応経過」追跡・無料登録への自然な導線。' },
];

// 報告入力欄の初期サンプル文（デモを毎回クリーンに開始できるよう定数化）
const DEFAULT_TEXT = '田中が3階の足場で作業中、手すりが少しぐらついていることに気づいた。荷物を運ぶ際に体が外側へふらついた。';

const CALLOUTS: Record<Screen, { n: number; text: string; top: string }[]> = {
  qr: [
    { n: 1, text: '現場に貼ったQRを読むだけ', top: '30%' },
    { n: 2, text: 'アプリ不要・ログイン不要で報告へ', top: '62%' },
  ],
  home: [
    { n: 1, text: 'チーム単位で安全活動を可視化', top: '18%' },
    { n: 2, text: '0件でも前向きに——ここに報告が貯まる', top: '46%' },
    { n: 3, text: '30秒・匿名で報告（AIが自動匿名化）', top: '74%' },
  ],
  input: [
    { n: 1, text: 'テキスト・音声・写真を等価に選べる', top: '26%' },
    { n: 2, text: '手袋でも押せる大きなボタン', top: '52%' },
    { n: 3, text: 'やさしい日本語・多言語の現場にも', top: '78%' },
  ],
  confirm: [
    { n: 1, text: '① 個人名をAIが自動で匿名化', top: '20%' },
    { n: 2, text: '③ 危険度を自動で判定', top: '46%' },
    { n: 3, text: '② 人・機械・材料・方法に自動分類', top: '72%' },
  ],
  success: [
    { n: 1, text: '達成感：あなたの声がチームへ', top: '18%' },
    { n: 2, text: '④ 投稿者本人が「対応経過」を確認できる（未対応→対応中→対応済み・匿名のまま）', top: '50%' },
  ],
};

const ICON = {
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
};

export const DemoExperience: React.FC<DemoExperienceProps> = ({ teamName = '東京第3工場 Bチーム', onBackToLp, onSignup, onTryWhyDemo }) => {
  const [screen, setScreen] = useState<Screen>('qr');
  const [method, setMethod] = useState<Method>('text');
  const [text, setText] = useState(DEFAULT_TEXT);
  const [rec, setRec] = useState<Rec>('idle');
  const [recSec, setRecSec] = useState(0);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  /* デモ画面のインラインエラー。alert() は現場のスマホで見逃されやすく、
     見込み客が最初に触る画面でもあるため、実フロー(WorkerFlow)と同じ方式に揃える。 */
  const [demoError, setDemoError] = useState<{ title: string; action: string } | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('AIが内容を分析中...');
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  // 本物のAI分析結果（入力に応じて実Geminiが返す）。確認画面はこれを描画する。
  const [analysis, setAnalysis] = useState<AIAnalysisResult | null>(null);
  const [rawInput, setRawInput] = useState(''); // 確認画面「あなたの入力（そのまま）」用

  const fileRef = useRef<HTMLInputElement>(null);
  const recTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const navTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  useEffect(() => () => {
    if (recTimer.current) clearInterval(recTimer.current);
    if (navTimer.current) clearTimeout(navTimer.current);
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    if (photoUrl) URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);

  const go = (s: Screen) => {
    if (navTimer.current) { clearTimeout(navTimer.current); navTimer.current = null; }
    setScreen(s);
    setProcessing(false);
  };

  // ---- 実マイク録音（本番 WorkerFlow と同じ実装） ----
  const stopRecording = () => {
    if (recTimer.current) { clearInterval(recTimer.current); recTimer.current = null; }
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
  };
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4',
      });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        setAudioBlob(new Blob(audioChunksRef.current, { type: mr.mimeType }));
        setRec('done');
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current = mr;
      mr.start(1000);
      setRec('rec');
      setRecSec(0);
      recTimer.current = setInterval(() => {
        setRecSec((v) => { if (v >= 60) { stopRecording(); return v; } return v + 1; });
      }, 1000);
    } catch {
      setDemoError({
        title: 'マイクが使えません',
        action: 'ブラウザの設定でこのサイトのマイクを許可してください。テキスト入力でも試せます。'
      });
      setRec('idle');
    }
  };
  const toggleRecord = () => {
    if (rec === 'rec') stopRecording();
    else { setAudioBlob(null); startRecording(); }
  };

  const onPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoUrl(URL.createObjectURL(f));
  };
  const removePhoto = () => { if (photoUrl) URL.revokeObjectURL(photoUrl); setPhotoUrl(null); };

  // 「次へ」: 本物の文字起こし + 実Gemini分析（org='demo'）。送信(DB保存)はしない＝本番デモと同じ。
  // 次の操作を始めたら前のエラーは消す。
  const inputNext = async () => {
    setDemoError(null);
    const typed = text.trim();
    if (!typed && !audioBlob) {
      setDemoError({ title: '報告内容が入力されていません', action: 'テキストまたは音声のどちらかを入力してください。' });
      return;
    }
    setProcessing(true);
    try {
      let transcribed = '';
      if (audioBlob) {
        setProcessingStep('音声を文字起こし中...');
        const tr = await transcribeAudio(audioBlob, 'demo');
        if (tr.success && tr.text) {
          transcribed = tr.text;
        } else {
          setDemoError({
            title: '音声を文字にできませんでした',
            action: '電波の良い場所でもう一度お試しいただくか、テキストで入力してください。'
          });
          setProcessing(false);
          return;
        }
      }
      const content = typed || transcribed;
      if (!content) {
        setDemoError({ title: '報告内容が入力されていません', action: 'テキストまたは音声のどちらかを入力してください。' });
        setProcessing(false);
        return;
      }
      setRawInput(content);
      setProcessingStep('AIが内容を分析中...');
      const result = await analyzeSafetyReport(text, transcribed, 'demo', undefined);
      setAnalysis(result);
      setProcessing(false);
      setScreen('confirm');
    } catch {
      setDemoError({
        title: '送信の準備ができませんでした',
        action: '入力した内容は消えていません。もう一度お試しください。'
      });
      setProcessing(false);
    }
  };
  const confirmSubmit = () => {
    // デモは DB 保存しない（本番デモと同じ）。完了画面へ。
    navTimer.current = setTimeout(() => setScreen('success'), reducedMotion() ? 200 : 650);
  };
  const resetForm = () => {
    setText(DEFAULT_TEXT);
    setAudioBlob(null);
    setRec('idle');
    setMethod('text');
    setAnalysis(null);
    if (photoUrl) { URL.revokeObjectURL(photoUrl); setPhotoUrl(null); }
  };
  const copyUrl = () => {
    const url = window.location.origin + '/my-report/a1b2c3d4-demo-4f5e-9a8b-7c6d5e4f3a2b';
    try { navigator.clipboard && navigator.clipboard.writeText(url); } catch { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const order = STEPS.map((s) => s.key);
  const curIdx = order.indexOf(screen);
  const showNext = curIdx < STEPS.length - 1;
  const goNext = () => { const ni = curIdx + 1; if (ni < order.length) go(order[ni]); };

  // analysis.category1 から 4M のどれを強調するか
  const fourMFor = (cat?: Category): { label: string; on: boolean }[] => {
    const c = String(cat || '');
    const isMan = c.includes('Man') || c.includes('人');
    const isMachine = c.includes('Machine') || c.includes('機械');
    const isMaterial = c.includes('Material') || c.includes('材料');
    return [
      { label: '人', on: isMan },
      { label: '機械・設備', on: isMachine },
      { label: '材料', on: isMaterial },
      { label: '方法', on: !isMan && !isMachine && !isMaterial },
    ];
  };
  const riskStyle = (r?: RiskLevel): string =>
    r === RiskLevel.HIGH ? 'color:#b91c1c;background:#fee2e2;border:1px solid #fecaca'
      : r === RiskLevel.LOW ? 'color:#15803d;background:#dcfce7;border:1px solid #bbf7d0'
      : 'color:#a16207;background:#fef9c3;border:1px solid #fde68a';
  const tagBase = 'font-size:11.5px;font-weight:700;border-radius:8px;padding:5px 11px;white-space:nowrap;';

  const methods: { key: Method; emoji: string; title: string; sub: string }[] = [
    { key: 'text', emoji: '✏️', title: 'テキスト', sub: '入力して報告' },
    { key: 'voice', emoji: '🎤', title: '音声', sub: '話して報告' },
    { key: 'photo', emoji: '📷', title: '写真', sub: '撮って報告' },
  ];
  const methodBase = 'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;padding:13px 6px;border-radius:13px;cursor:pointer;transition:all .15s ease;';

  const svg = (props: React.SVGProps<SVGSVGElement>, children: React.ReactNode) => (
    <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>{children}</svg>
  );

  return (
    <div className="ap-demo">
      <style>{DEMO_CSS}</style>
      <div data-stage style={sx('position:relative;min-height:100vh;display:flex;flex-direction:column;align-items:center;padding:54px clamp(12px,3vw,32px) 14px;background:linear-gradient(180deg,#f1f5f9,#e6ebf2)')}>

        {/* back to LP — デスクトップは画面左上に絶対配置（縦スペースを食わない）。モバイルは通常フローに戻す（DEMO_CSS） */}
        <div data-backbar style={sx('position:absolute;top:14px;left:clamp(12px,3vw,32px);z-index:20;margin:0')}>
          <button data-cta="back-to-lp" onClick={onBackToLp} className="hover:bg-slate-100" style={sx('display:inline-flex;align-items:center;gap:7px;font-size:13px;font-weight:700;color:#475569;background:#fff;border:1px solid #e2e8f0;border-radius:999px;padding:8px 15px;box-shadow:0 2px 8px -4px rgba(15,23,42,.2);cursor:pointer;transition:background .15s ease')}>
            {svg({ width: 16, height: 16, viewBox: '0 0 24 24' }, <><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></>)}
            安全ポスト＋ トップ（LP）へ戻る
          </button>
        </div>

        {/* MOBILE demo banner */}
        <div data-mobileonly style={{ ...sx('display:none;width:100%;background:#eff6ff;border-bottom:1px solid #dbeafe;padding:14px 16px') }}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px')}>
            <span style={sx('font-size:11px;font-weight:800;letter-spacing:.06em;color:#fff;background:#2563eb;border-radius:6px;padding:3px 9px')}>DEMO</span>
            <span style={sx('font-size:14px;font-weight:800;color:#1e3a8a')}>これはデモ画面です（本番環境ではありません）</span>
            <span style={sx('display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:800;color:#c2410c;background:#fff7ed;border:1px solid #ffedd5;border-radius:999px;padding:3px 9px;margin-left:auto')}>
              {svg({ width: 12, height: 12, viewBox: '0 0 24 24', strokeWidth: 2.2 }, <><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2" /><path d="M9 2h6" /></>)}30秒
            </span>
          </div>
          <ul style={sx('list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:4px')}>
            {DEMO_BULLETS.map((b) => (
              <li key={b.n} style={sx('font-size:12px;color:#1d4ed8;line-height:1.5')}>・{b.text}</li>
            ))}
          </ul>
          <p style={sx('margin:8px 0 0;font-size:11px;font-style:italic;color:#93a3c4')}>※デモモードの報告はデータベースに保存されません</p>
        </div>

        {/* DESKTOP demo banner */}
        <div data-deskonly style={sx('width:100%;max-width:1180px;background:#eff6ff;border:1.5px solid #93c5fd;border-radius:16px;padding:9px 18px;display:flex;align-items:center;gap:18px;flex-wrap:wrap;box-shadow:0 8px 24px -14px rgba(37,99,235,.32)')}>
          <div style={sx('display:flex;align-items:center;gap:11px;flex:0 0 auto')}>
            <span style={sx('font-size:12px;font-weight:800;letter-spacing:.08em;color:#fff;background:#2563eb;border-radius:8px;padding:5px 11px')}>DEMO</span>
            <div>
              <div style={sx('font-size:15px;font-weight:800;color:#1e3a8a;line-height:1.3')}>これはデモ画面です<span style={sx('color:#3b82f6;font-weight:600')}>（本番環境ではありません）</span></div>
              <div style={sx('font-size:11px;font-style:italic;color:#7891bd;margin-top:1px')}>※デモモードの報告はデータベースに保存されません</div>
            </div>
          </div>
          <ul style={sx('list-style:none;margin:0;padding:0;display:flex;gap:18px;flex-wrap:wrap;flex:1 1 360px')}>
            {DEMO_BULLETS.map((b) => (
              <li key={b.n} style={sx('display:flex;align-items:flex-start;gap:7px;font-size:12.5px;color:#1d4ed8;line-height:1.45;max-width:240px')}>
                <span style={sx('flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;border-radius:50%;background:#dbeafe;color:#1d4ed8;font-size:10px;font-weight:800;margin-top:1px')}>{b.n}</span>
                <span>{b.text}</span>
              </li>
            ))}
          </ul>
          <span style={sx('display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:800;color:#c2410c;background:#fff7ed;border:1px solid #ffedd5;border-radius:999px;padding:7px 13px;flex:0 0 auto')}>
            {svg({ width: 14, height: 14, viewBox: '0 0 24 24', strokeWidth: 2.2 }, <><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2" /><path d="M9 2h6" /></>)}
            約30秒で体験
          </span>
        </div>

        {/* TOUR: rail + phone + callouts */}
        <div data-tour style={sx('position:relative;display:flex;align-items:flex-start;justify-content:center;gap:clamp(20px,3vw,48px);width:100%;margin-top:clamp(8px,1.2vw,12px)')}>

          {/* LEFT rail */}
          <div data-deskonly style={sx('flex:0 0 330px;max-width:330px;display:flex;flex-direction:column;gap:7px;padding-top:2px')}>
            <div style={sx('font-size:12px;font-weight:800;letter-spacing:.08em;color:#ea580c;margin-bottom:3px')}>画面ガイド</div>
            <p style={sx('margin:0 0 5px;font-size:11.5px;color:#64748b;line-height:1.5')}>下のステップを<strong style={sx('color:#334155;font-weight:700')}>クリック</strong>すると各画面に移動します。スマホ内のボタン、または<strong style={sx('color:#334155;font-weight:700')}>「次のステップを見る」</strong>でも進めます。</p>
            {STEPS.map((st, i) => {
              const active = curIdx === i;
              const done = curIdx > i;
              const isNext = i === curIdx + 1;
              // NOTE: border は使わず4辺ロングハンドで指定（border + border-left 併用は React の
              // shorthand/longhand 競合警告を出すため。状態切替で left だけ強調する設計）。
              const sideBorder = (c: string) => `border-top:1px solid ${c};border-right:1px solid ${c};border-bottom:1px solid ${c};`;
              const cardStyle = 'display:flex;align-items:center;gap:12px;width:100%;text-align:left;cursor:pointer;border-radius:14px;padding:10px 14px;transition:all .18s ease;' +
                (active ? 'background:#fff;' + sideBorder('#e2e8f0') + 'border-left:3px solid #f97316;box-shadow:0 14px 30px -18px rgba(15,23,42,.35)'
                  : isNext ? 'background:#fff7ed;' + sideBorder('#fed7aa') + 'border-left:3px solid #f97316'
                  : 'background:rgba(255,255,255,.45);' + sideBorder('transparent') + 'border-left:3px solid transparent');
              const numStyle = 'flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:9px;font-size:14px;font-weight:800;' +
                (active ? 'background:#ea580c;color:#fff' : done ? 'background:#d1fae5;color:#059669' : isNext ? 'background:#ea580c;color:#fff;animation:apPulseRing 1.4s ease-in-out infinite' : 'background:#e2e8f0;color:#94a3b8');
              const titleStyle = 'font-size:15px;font-weight:800;' + (active ? 'color:#0f172a' : isNext ? 'color:#9a3412' : 'color:#475569');
              const descStyle = 'font-size:12.5px;line-height:1.55;' + (active ? 'color:#475569' : 'color:#94a3b8');
              const statusText = active ? '現在' : done ? '✓' : isNext ? 'クリック' : '›';
              const statusStyle = 'margin-left:auto;flex:0 0 auto;align-self:center;' + (active
                ? 'font-size:10px;font-weight:800;color:#fff;background:#ea580c;border-radius:999px;padding:3px 9px'
                : done ? 'font-size:14px;font-weight:800;color:#059669'
                : isNext ? 'font-size:10px;font-weight:800;color:#ea580c;border:1.5px solid #fdba74;border-radius:999px;padding:3px 9px;animation:apBlink 1.2s ease-in-out infinite'
                : 'font-size:18px;font-weight:800;color:#cbd5e1');
              return (
                <button key={st.key} onClick={() => go(st.key)} style={sx(cardStyle)}>
                  <span style={sx(numStyle)}>{st.num}</span>
                  <span style={sx('display:flex;flex-direction:column;gap:3px;min-width:0')}>
                    <span style={sx(titleStyle)}>{st.title}</span>
                    <span style={sx(descStyle)}>{st.desc}</span>
                  </span>
                  <span style={sx(statusStyle)}>{statusText}</span>
                </button>
              );
            })}

            {showNext && (
              <button onClick={goNext} className="hover:-translate-y-0.5" style={sx('margin-top:8px;display:flex;align-items:center;justify-content:center;gap:9px;width:100%;cursor:pointer;border:none;border-radius:13px;padding:15px;background:linear-gradient(120deg,#f97316,#ea580c);color:#fff;font-size:15px;font-weight:800;box-shadow:0 16px 30px -14px rgba(249,115,22,.7);transition:transform .15s ease')}>
                次のステップを見る
                {svg({ width: 18, height: 18, viewBox: '0 0 24 24', strokeWidth: 2.6, style: sx('animation:apNudge 1.1s ease-in-out infinite') }, <polyline points="9 18 15 12 9 6" />)}
              </button>
            )}

            {screen === 'success' && (
              <button data-cta="signup" onClick={onSignup} className="hover:-translate-y-0.5 text-left" style={sx('margin-top:8px;display:flex;flex-direction:column;gap:5px;background:linear-gradient(120deg,#f97316,#eab308);border:none;cursor:pointer;border-radius:14px;padding:15px 16px;box-shadow:0 16px 32px -18px rgba(249,115,22,.6);transition:transform .15s ease')}>
                <span style={sx('font-size:11px;font-weight:800;letter-spacing:.04em;color:rgba(255,255,255,.92)')}>デモ完了！ 次のステップ</span>
                <span style={sx('font-size:15px;font-weight:800;color:#fff')}>無料で始める（カード不要）→</span>
                <span style={sx('font-size:11px;color:rgba(255,255,255,.94);line-height:1.5')}>QRコード3個・AI分析10回/月まで、ずっと¥0で。</span>
              </button>
            )}
          </div>

          <div data-phonewrap style={sx('display:flex;flex-direction:column;align-items:center;gap:14px')}>
            <div data-phone style={sx('position:relative;width:392px;background:linear-gradient(180deg,#1e293b,#0f172a);border-radius:46px;padding:12px;box-shadow:0 50px 90px -34px rgba(0,0,0,.75),0 0 0 1px rgba(148,163,184,.14)')}>

              {/* side buttons */}
              <span data-deskonly style={sx('position:absolute;left:-3px;top:118px;width:3px;height:34px;border-radius:3px;background:#334155')} />
              <span data-deskonly style={sx('position:absolute;left:-3px;top:166px;width:3px;height:58px;border-radius:3px;background:#334155')} />
              <span data-deskonly style={sx('position:absolute;right:-3px;top:150px;width:3px;height:66px;border-radius:3px;background:#334155')} />

              {/* gutter callouts */}
              {CALLOUTS[screen].map((co) => (
                <div key={co.n} data-deskonly style={sx('position:absolute;z-index:5;width:200px;display:flex;align-items:flex-start;gap:9px;left:100%;margin-left:26px;top:' + co.top + ';background:#fff;border:1px solid #e2e8f0;border-radius:13px;padding:11px 13px;box-shadow:0 16px 34px -20px rgba(15,23,42,.45)')}>
                  <span style={sx('position:absolute;right:100%;top:50%;width:24px;height:2px;background:#f97316')} />
                  <span style={sx('position:absolute;right:calc(100% + 22px);top:50%;transform:translateY(-50%);width:7px;height:7px;border-radius:50%;background:#f97316')} />
                  <span style={sx('flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:25px;height:25px;border-radius:50%;background:#f97316;color:#fff;font-size:13.5px;font-weight:800')}>{co.n}</span>
                  <span style={sx('font-size:13.5px;font-weight:600;color:#0f172a;line-height:1.45')}>{co.text}</span>
                </div>
              ))}

              {/* notch */}
              <div data-deskonly style={sx('position:absolute;top:12px;left:50%;transform:translateX(-50%);width:120px;height:26px;background:#0f172a;border-radius:0 0 16px 16px;z-index:4')} />

              <div data-screen-card style={sx('position:relative;background:#f8fafc;border-radius:34px;overflow:hidden;display:flex;flex-direction:column;height:min(768px, calc(100vh - 250px))')}>

                {/* status bar */}
                <div data-deskonly style={sx('flex:0 0 auto;background:#0f172a;display:flex;align-items:center;justify-content:space-between;padding:8px 22px 4px')}>
                  <span style={sx('color:#fff;font-size:11px;font-weight:600')}>9:41</span>
                  <span style={sx('display:flex;align-items:center;gap:5px')}>
                    <svg width="15" height="11" viewBox="0 0 24 18" fill="#fff"><rect x="0" y="10" width="3" height="8" rx="1" /><rect x="5" y="6" width="3" height="12" rx="1" /><rect x="10" y="3" width="3" height="15" rx="1" /><rect x="15" y="0" width="3" height="18" rx="1" /></svg>
                    <span style={sx('width:16px;height:9px;border:1px solid #fff;border-radius:2px;display:inline-flex;align-items:center;padding:1px')}><span style={sx('width:11px;height:100%;background:#fff;border-radius:1px;display:block')} /></span>
                  </span>
                </div>

                {/* app header */}
                <div style={sx('flex:0 0 auto;background:#0f172a;display:flex;align-items:center;justify-content:center;gap:8px;padding:8px 16px 12px')}>
                  <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:linear-gradient(135deg,#f97316,#eab308);color:#fff')}>{svg({ width: 13, height: 13, viewBox: '0 0 24 24', strokeWidth: 2.2 }, ICON.shield)}</span>
                  <span style={sx('color:#fff;font-weight:800;font-size:16px')}>安全ポスト＋</span>
                </div>

                {/* scroll area */}
                <div style={sx('flex:1 1 auto;overflow-y:auto;position:relative')}>

                  {/* QR SCAN */}
                  {screen === 'qr' && (
                    <div style={sx('height:100%;min-height:600px;position:relative;background:#0b1220;display:flex;flex-direction:column')}>
                      <div style={sx('padding:20px 18px 12px;text-align:center')}>
                        <p style={sx('margin:0;font-size:15px;font-weight:800;color:#e2e8f0')}>現場のQRコードを枠内に合わせてください</p>
                        <p style={sx('margin:7px 0 0;font-size:12px;color:#475569;line-height:1.6')}>アプリ不要・ログイン不要。読み取るだけで報告できます。</p>
                      </div>
                      <div style={sx('flex:1;position:relative;display:flex;align-items:center;justify-content:center;overflow:hidden')}>
                        <div style={sx('position:absolute;inset:0;background:radial-gradient(circle at 50% 44%, rgba(30,41,59,.15), rgba(2,6,23,.88))')} />
                        <div style={sx('position:relative;width:238px;height:238px')}>
                          <span style={sx('position:absolute;top:0;left:0;width:38px;height:38px;border-top:4px solid #f97316;border-left:4px solid #f97316;border-radius:9px 0 0 0')} />
                          <span style={sx('position:absolute;top:0;right:0;width:38px;height:38px;border-top:4px solid #f97316;border-right:4px solid #f97316;border-radius:0 9px 0 0')} />
                          <span style={sx('position:absolute;bottom:0;left:0;width:38px;height:38px;border-bottom:4px solid #f97316;border-left:4px solid #f97316;border-radius:0 0 0 9px')} />
                          <span style={sx('position:absolute;bottom:0;right:0;width:38px;height:38px;border-bottom:4px solid #f97316;border-right:4px solid #f97316;border-radius:0 0 9px 0')} />
                          <div style={sx('position:absolute;inset:26px;background:#fff;border-radius:12px;box-shadow:0 22px 44px -16px rgba(0,0,0,.65);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;transform:rotate(-2deg)')}>
                            <div style={sx('display:flex;align-items:center;gap:6px')}>
                              <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:5px;background:linear-gradient(135deg,#f97316,#eab308);color:#fff')}>{svg({ width: 11, height: 11, viewBox: '0 0 24 24', strokeWidth: 2.2 }, ICON.shield)}</span>
                              <span style={sx('font-size:12px;font-weight:800;color:#0f172a')}>安全ポスト＋</span>
                            </div>
                            <svg width="128" height="128" viewBox="0 0 100 100" fill="#0f172a" aria-hidden="true">
                              <rect x="0" y="0" width="28" height="28" /><rect x="4" y="4" width="20" height="20" fill="#fff" /><rect x="8" y="8" width="12" height="12" />
                              <rect x="72" y="0" width="28" height="28" /><rect x="76" y="4" width="20" height="20" fill="#fff" /><rect x="80" y="8" width="12" height="12" />
                              <rect x="0" y="72" width="28" height="28" /><rect x="4" y="76" width="20" height="20" fill="#fff" /><rect x="8" y="80" width="12" height="12" />
                              <rect x="36" y="8" width="8" height="8" /><rect x="52" y="8" width="8" height="8" /><rect x="44" y="20" width="8" height="8" /><rect x="60" y="20" width="8" height="8" />
                              <rect x="8" y="36" width="8" height="8" /><rect x="24" y="36" width="8" height="8" /><rect x="40" y="36" width="8" height="8" /><rect x="64" y="36" width="8" height="8" /><rect x="84" y="36" width="8" height="8" />
                              <rect x="36" y="48" width="8" height="8" /><rect x="52" y="48" width="8" height="8" /><rect x="72" y="48" width="8" height="8" />
                              <rect x="40" y="64" width="8" height="8" /><rect x="56" y="64" width="8" height="8" /><rect x="84" y="64" width="8" height="8" />
                              <rect x="36" y="80" width="8" height="8" /><rect x="52" y="80" width="8" height="8" /><rect x="72" y="84" width="8" height="8" /><rect x="88" y="80" width="8" height="8" />
                              <rect x="36" y="92" width="8" height="8" /><rect x="60" y="92" width="8" height="8" />
                            </svg>
                            <span style={sx('font-size:11px;font-weight:700;color:#475569')}>安全報告はこちら</span>
                          </div>
                          <span style={sx('position:absolute;left:12px;right:12px;top:14px;height:2px;background:linear-gradient(90deg,transparent,#f97316,transparent);box-shadow:0 0 12px #f97316;animation:apScan 2.4s ease-in-out infinite')} />
                        </div>
                      </div>
                      <div style={sx('padding:14px 18px 24px;background:linear-gradient(180deg,transparent,#0b1220 45%)')}>
                        <button data-cta="demo-scan" onClick={() => go('home')} className="hover:bg-[#c2410c] active:scale-[0.97]" style={sx('width:100%;display:flex;align-items:center;justify-content:center;gap:9px;background:#ea580c;border:none;cursor:pointer;color:#fff;font-weight:800;font-size:16px;padding:15px;border-radius:14px;box-shadow:0 14px 28px -12px rgba(234,88,12,.7);transition:background .15s ease,transform .12s ease')}>
                          {svg({ width: 20, height: 20, viewBox: '0 0 24 24' }, <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><line x1="14" y1="14" x2="14" y2="17" /><line x1="17" y1="14" x2="21" y2="14" /><line x1="21" y1="17" x2="21" y2="21" /><line x1="14" y1="21" x2="17" y2="21" /></>)}
                          スキャンして報告をはじめる
                        </button>
                        <p style={sx('text-align:center;font-size:11px;color:#64748b;margin:11px 0 0')}>※ 実際の現場ではスマホ標準カメラでQRを読み取ります</p>
                      </div>
                    </div>
                  )}

                  {/* HOME */}
                  {screen === 'home' && (
                    <div style={sx('padding:22px 18px 26px;animation:apFade .35s ease both')}>
                      <button data-cta="demo-home" onClick={() => go('qr')} style={sx('display:inline-flex;align-items:center;gap:4px;background:none;border:none;cursor:pointer;font-size:13px;font-weight:600;color:#94a3b8;padding:0 0 8px')}>
                        {svg({ width: 16, height: 16, viewBox: '0 0 24 24', strokeWidth: 2.2 }, <polyline points="15 18 9 12 15 6" />)}ホーム
                      </button>
                      <div style={sx('text-align:center;margin:18px 0 22px')}>
                        <h2 style={sx('margin:0 0 6px;font-size:22px;font-weight:800;color:#0f172a')}>{teamName}</h2>
                        <p style={sx('margin:0;font-size:13px;color:#64748b')}>安全活動にご協力ありがとうございます。</p>
                      </div>
                      <div style={sx('background:#fff7ed;border:1px solid #ffedd5;border-radius:16px;padding:18px;margin-bottom:22px')}>
                        <div style={sx('display:flex;align-items:center;justify-content:space-between;margin-bottom:14px')}>
                          <span style={sx('display:inline-flex;align-items:center;gap:7px;font-size:14px;font-weight:800;color:#9a3412')}>
                            {svg({ width: 17, height: 17, viewBox: '0 0 24 24' }, <><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>)}
                            今月の安全状況
                          </span>
                          <span style={sx('font-size:10px;font-weight:800;color:#c2410c;background:#ffedd5;border-radius:999px;padding:3px 9px')}>今月これから</span>
                        </div>
                        <div style={sx('display:flex;justify-content:space-around;text-align:center;margin-bottom:14px')}>
                          <div>
                            <div style={sx('font-size:26px;font-weight:800;color:#0f172a;line-height:1')}>0<span style={sx('font-size:13px;font-weight:600;color:#94a3b8;margin-left:2px')}>件</span></div>
                            <div style={sx('font-size:11px;color:#475569;margin-top:4px')}>今月の報告</div>
                          </div>
                          <div style={sx('width:1px;background:#fed7aa')} />
                          <div>
                            <div style={sx('font-size:26px;font-weight:800;color:#16a34a;line-height:1')}>0<span style={sx('font-size:13px;font-weight:600;color:#94a3b8;margin-left:2px')}>件</span></div>
                            <div style={sx('font-size:11px;color:#475569;margin-top:4px')}>今月の改善</div>
                          </div>
                        </div>
                        <div style={sx('display:flex;align-items:center;gap:9px;background:#fff;border:1px dashed #fdba74;border-radius:12px;padding:11px 13px')}>
                          <span style={sx('flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:10px;background:#fff7ed;color:#ea580c')}>{svg({ width: 18, height: 18, viewBox: '0 0 24 24' }, <><path d="M12 5v14" /><path d="M5 12h14" /></>)}</span>
                          <p style={sx('margin:0;font-size:12px;color:#7c2d12;line-height:1.5')}>まだ報告はありません。<strong style={sx('font-weight:700')}>最初の報告</strong>がここに記録されていきます。</p>
                        </div>
                      </div>
                      <button data-cta="demo-start-report" onClick={() => go('input')} className="hover:bg-[#c2410c] active:scale-[0.97]" style={sx('width:100%;display:flex;align-items:center;justify-content:center;gap:9px;background:#ea580c;border:none;cursor:pointer;color:#fff;font-weight:800;font-size:17px;padding:16px;border-radius:14px;box-shadow:0 14px 28px -12px rgba(234,88,12,.7);transition:background .15s ease,transform .12s ease')}>
                        {svg({ width: 20, height: 20, viewBox: '0 0 24 24' }, <><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>)}
                        報告・提案をする
                      </button>
                      <p style={sx('text-align:center;font-size:11px;color:#475569;margin:12px 0 0')}>※個人は特定されません（AIが自動匿名化）</p>
                    </div>
                  )}

                  {/* INPUT */}
                  {screen === 'input' && !processing && (
                    <div style={sx('padding:18px;animation:apFade .35s ease both')}>
                      <div style={sx('display:flex;align-items:center;justify-content:center;gap:0;margin-bottom:18px')}>
                        <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:#ea580c;color:#fff;font-size:12px;font-weight:800')}>1</span>
                        <span style={sx('margin-left:7px;font-size:13px;font-weight:700;color:#ea580c')}>入力</span>
                        <span style={sx('width:30px;height:2px;background:#e2e8f0;margin:0 10px')} />
                        <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:#e2e8f0;color:#94a3b8;font-size:12px;font-weight:800')}>2</span>
                        <span style={sx('margin-left:7px;font-size:13px;color:#94a3b8')}>確認</span>
                      </div>
                      <h3 style={sx('margin:0 0 14px;font-size:18px;font-weight:800;color:#0f172a')}>報告内容の入力</h3>
                      <div style={sx('display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:16px')}>
                        {methods.map((m) => {
                          const on = method === m.key;
                          return (
                            <button key={m.key} onClick={() => setMethod(m.key)} style={sx(methodBase + (on ? 'background:#fff7ed;border:1.5px solid #f97316;box-shadow:0 8px 18px -12px rgba(249,115,22,.6)' : 'background:#fff;border:1px solid #e2e8f0'))}>
                              <span style={sx('font-size:22px;line-height:1')}>{m.emoji}</span>
                              <span style={sx('font-size:13px;font-weight:800;color:#0f172a')}>{m.title}</span>
                              <span style={sx('font-size:10px;color:#94a3b8')}>{m.sub}</span>
                            </button>
                          );
                        })}
                      </div>

                      {method === 'text' && (
                        <div style={sx('margin-bottom:18px')}>
                          <label style={sx('display:block;font-size:13px;font-weight:700;color:#334155;margin-bottom:7px')}>気付いたこと・ヒヤリとしたこと <span style={sx('color:#ef4444')}>*</span></label>
                          <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="例）3階の足場で作業中、手すりが少しぐらついているのに気づいた。" style={sx('width:100%;min-height:128px;padding:13px;border:1px solid #cbd5e1;border-radius:13px;font-size:14px;font-family:inherit;color:#0f172a;resize:none;outline:none;line-height:1.6')} />
                          <p style={sx('text-align:right;font-size:11px;color:#94a3b8;margin:5px 0 0')}>{text.length}/2000</p>
                        </div>
                      )}

                      {method === 'voice' && (
                        <button onClick={toggleRecord} style={sx('position:relative;width:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:26px 16px;border-radius:14px;cursor:pointer;margin-bottom:18px;transition:all .15s ease;' +
                          (rec === 'rec' ? 'background:#fef2f2;border:1.5px solid #ef4444;color:#dc2626' : rec === 'done' ? 'background:#ecfdf5;border:1.5px solid #10b981;color:#059669' : 'background:#fff;border:1px solid #e2e8f0;color:#475569'))}>
                          {rec === 'idle' && (<>
                            {svg({ width: 30, height: 30, viewBox: '0 0 24 24', strokeWidth: 1.8, style: sx('opacity:.7;margin-bottom:8px') }, <><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="22" /></>)}
                            <span style={sx('font-size:13px;font-weight:800')}>音声入力</span>
                            <span style={sx('font-size:11px;opacity:.7;margin-top:2px')}>タップして録音開始</span>
                          </>)}
                          {rec === 'rec' && (<>
                            <span style={sx('position:absolute;top:12px;right:12px;width:9px;height:9px;border-radius:50%;background:#dc2626;animation:apPing 1.1s ease-out infinite')} />
                            {svg({ width: 30, height: 30, viewBox: '0 0 24 24', strokeWidth: 1.8, style: sx('margin-bottom:8px') }, <><circle cx="12" cy="12" r="10" /><rect x="9" y="9" width="6" height="6" rx="1" /></>)}
                            <span style={sx('font-size:20px;font-weight:800;font-variant-numeric:tabular-nums')}>{fmtTime(recSec)}</span>
                            <span style={sx('font-size:11px;font-weight:600;margin-top:2px')}>タップで停止</span>
                          </>)}
                          {rec === 'done' && (<>
                            {svg({ width: 30, height: 30, viewBox: '0 0 24 24', style: sx('margin-bottom:8px') }, <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>)}
                            <span style={sx('font-size:13px;font-weight:800')}>録音完了</span>
                            <span style={sx('font-size:11px;opacity:.7;margin-top:2px')}>タップで再録音</span>
                          </>)}
                        </button>
                      )}

                      {method === 'photo' && (
                        <div style={sx('margin-bottom:18px')}>
                          <input type="file" accept="image/*" ref={fileRef} onChange={onPhotoChange} style={sx('display:none')} />
                          {photoUrl ? (
                            <div style={sx('position:relative;border-radius:14px;overflow:hidden;border:1px solid #e2e8f0')}>
                              <img src={photoUrl} alt="プレビュー" style={sx('width:100%;height:190px;object-fit:cover;display:block')} />
                              <button onClick={removePhoto} style={sx('position:absolute;top:9px;right:9px;width:28px;height:28px;border:none;cursor:pointer;border-radius:50%;background:#ef4444;color:#fff;display:flex;align-items:center;justify-content:center')}>{svg({ width: 15, height: 15, viewBox: '0 0 24 24', strokeWidth: 2.5 }, <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>)}</button>
                              <span style={sx('position:absolute;bottom:9px;left:9px;display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:700;color:#fff;background:rgba(15,23,42,.7);border-radius:7px;padding:4px 9px')}>{svg({ width: 12, height: 12, viewBox: '0 0 24 24' }, <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>)}写真添付済み</span>
                            </div>
                          ) : (
                            <button onClick={() => fileRef.current?.click()} className="hover:bg-slate-50" style={sx('width:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;padding:30px 16px;border:1px dashed #cbd5e1;border-radius:14px;background:#fff;cursor:pointer;color:#64748b')}>
                              {svg({ width: 30, height: 30, viewBox: '0 0 24 24', strokeWidth: 1.8, style: sx('opacity:.7;margin-bottom:5px') }, <><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /><circle cx="12" cy="13" r="3.5" /></>)}
                              <span style={sx('font-size:13px;font-weight:800')}>写真を追加</span>
                              <span style={sx('font-size:10px;opacity:.7')}>撮影またはギャラリーから選択</span>
                            </button>
                          )}
                        </div>
                      )}

                      {demoError && (
                        <div role="alert" style={sx('display:flex;gap:8px;align-items:flex-start;padding:12px;background:#fef2f2;border:1px solid #fecaca;border-radius:13px;margin-bottom:4px')}>
                          <span style={sx('flex:none;width:18px;height:18px;border-radius:9px;background:#dc2626;color:#fff;font-size:12px;font-weight:800;display:flex;align-items:center;justify-content:center;margin-top:1px')}>!</span>
                          <div style={sx('flex:1')}>
                            <p style={sx('margin:0;font-size:13px;font-weight:800;color:#991b1b')}>{demoError.title}</p>
                            <p style={sx('margin:2px 0 0;font-size:12px;color:#b91c1c;line-height:1.6')}>{demoError.action}</p>
                          </div>
                          <button type="button" aria-label="エラー表示を閉じる" onClick={() => setDemoError(null)} style={sx('flex:none;border:none;background:none;cursor:pointer;color:#f87171;font-size:16px;line-height:1;padding:2px')}>×</button>
                        </div>
                      )}

                      <div style={sx('display:flex;gap:10px;margin-top:4px')}>
                        <button data-cta="demo-home" onClick={() => go('home')} className="hover:bg-slate-50" style={sx('flex:1;padding:13px;border:1px solid #cbd5e1;border-radius:13px;background:#fff;cursor:pointer;font-size:14px;font-weight:600;color:#475569')}>戻る</button>
                        <button data-cta="demo-input-next" onClick={inputNext} className="hover:bg-[#c2410c] active:scale-[0.97]" style={sx('flex:1;display:flex;align-items:center;justify-content:center;gap:5px;padding:13px;border:none;border-radius:13px;background:#ea580c;cursor:pointer;font-size:14px;font-weight:800;color:#fff;box-shadow:0 10px 22px -12px rgba(234,88,12,.7)')}>
                          次へ{svg({ width: 17, height: 17, viewBox: '0 0 24 24', strokeWidth: 2.4 }, <polyline points="9 18 15 12 9 6" />)}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* PROCESSING */}
                  {processing && (
                    <div style={sx('position:absolute;inset:0;background:rgba(248,250,252,.97);display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:30px;z-index:6;animation:apFade .25s ease both')}>
                      <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth={2} strokeLinecap="round" style={sx('animation:apSpin 1s linear infinite;margin-bottom:18px')}><path d="M21 12a9 9 0 1 1-6.2-8.5" /></svg>
                      <p style={sx('margin:0;font-size:15px;font-weight:800;color:#0f172a')}>{processingStep}</p>
                      <p style={sx('margin:8px 0 0;font-size:13px;color:#64748b')}>個人情報の匿名化処理を行っています</p>
                      <p style={sx('margin:3px 0 0;font-size:11px;color:#94a3b8')}>この処理は数秒で完了します</p>
                    </div>
                  )}

                  {/* CONFIRM */}
                  {screen === 'confirm' && (
                    <div style={sx('padding:18px;animation:apFade .35s ease both')}>
                      <div style={sx('display:flex;align-items:center;justify-content:center;gap:0;margin-bottom:18px')}>
                        <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:#10b981;color:#fff')}>{svg({ width: 14, height: 14, viewBox: '0 0 24 24', strokeWidth: 3 }, <polyline points="20 6 9 17 4 12" />)}</span>
                        <span style={sx('margin-left:7px;font-size:13px;color:#64748b')}>入力</span>
                        <span style={sx('width:30px;height:2px;background:#ea580c;margin:0 10px')} />
                        <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:#ea580c;color:#fff;font-size:12px;font-weight:800')}>2</span>
                        <span style={sx('margin-left:7px;font-size:13px;font-weight:700;color:#ea580c')}>確認</span>
                      </div>
                      <h3 style={sx('margin:0 0 14px;font-size:18px;font-weight:800;color:#0f172a')}>AIが整形した内容を確認</h3>

                      <div style={sx('border:1px solid #e2e8f0;border-radius:13px;padding:13px;margin-bottom:6px;background:#fff')}>
                        <div style={sx('font-size:10px;font-weight:800;letter-spacing:.04em;color:#94a3b8;margin-bottom:7px')}>あなたの入力（そのまま）</div>
                        <p style={sx('margin:0;font-size:12.5px;color:#475569;line-height:1.7;white-space:pre-line')}>{rawInput || text}</p>
                      </div>
                      <div style={sx('display:flex;align-items:center;justify-content:center;gap:7px;margin:4px 0')}>
                        <span style={sx('height:1px;flex:1;background:#e2e8f0')} />
                        <span style={sx('display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:800;color:#c2410c;background:#fff7ed;border:1px solid #ffedd5;border-radius:999px;padding:4px 11px')}>
                          {svg({ width: 12, height: 12, viewBox: '0 0 24 24' }, <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" />)}
                          AIが匿名化・整形
                        </span>
                        <span style={sx('height:1px;flex:1;background:#e2e8f0')} />
                      </div>

                      <div style={sx('border:1.5px solid #fed7aa;border-radius:13px;padding:14px;background:linear-gradient(180deg,#fffbf5,#fff)')}>
                        <div style={sx('margin-bottom:12px')}>
                          <div style={sx('font-size:12px;font-weight:700;color:#64748b;margin-bottom:4px')}>タイトル</div>
                          <div style={sx('font-size:15px;font-weight:800;color:#0f172a')}>{analysis?.title}</div>
                        </div>
                        <div style={sx('margin-bottom:12px')}>
                          <div style={sx('font-size:12px;font-weight:700;color:#64748b;margin-bottom:4px')}>整形された本文（AIが個人名などを自動で匿名化）</div>
                          <p style={sx('margin:0;font-size:13px;color:#334155;line-height:1.7;white-space:pre-line')}>{analysis?.formattedBody}</p>
                        </div>
                        <div style={sx('display:flex;gap:14px;margin-bottom:12px')}>
                          <div>
                            <div style={sx('font-size:11px;font-weight:700;color:#94a3b8;margin-bottom:5px')}>危険度</div>
                            <span style={sx('display:inline-flex;align-items:center;font-size:13px;font-weight:800;border-radius:999px;padding:3px 12px;' + riskStyle(analysis?.riskLevel))}>{analysis?.riskLevel}</span>
                          </div>
                          <div>
                            <div style={sx('font-size:11px;font-weight:700;color:#94a3b8;margin-bottom:5px')}>カテゴリ</div>
                            <span style={sx('font-size:13px;font-weight:700;color:#334155')}>{analysis?.category1}</span>
                          </div>
                        </div>
                        <div style={sx('margin-bottom:12px')}>
                          <div style={sx('font-size:11px;font-weight:700;color:#94a3b8;margin-bottom:6px')}>4M分類</div>
                          <div style={sx('display:flex;gap:6px;flex-wrap:wrap')}>
                            {fourMFor(analysis?.category1).map((t) => (
                              <span key={t.label} style={sx(tagBase + (t.on ? 'color:#c2410c;background:#fff7ed;border:1px solid #fdba74' : 'color:#94a3b8;background:#fff;border:1px solid #e2e8f0'))}>{t.label}</span>
                            ))}
                          </div>
                        </div>
                        {analysis?.category2 && (
                          <div style={sx('margin-bottom:12px')}>
                            <div style={sx('font-size:11px;font-weight:700;color:#94a3b8;margin-bottom:5px')}>2次カテゴリ</div>
                            <span style={sx('display:inline-flex;font-size:12px;font-weight:600;color:#475569;background:#f1f5f9;border-radius:7px;padding:4px 9px')}>#{analysis.category2}</span>
                          </div>
                        )}
                        {analysis?.lesson && (
                          <div>
                            <div style={sx('font-size:11px;font-weight:700;color:#94a3b8;margin-bottom:5px')}>教訓</div>
                            <div style={sx('background:#eff6ff;border:1px solid #dbeafe;border-radius:11px;padding:11px 12px;font-size:12.5px;color:#1e40af;line-height:1.7;white-space:pre-line')}>{analysis.lesson}</div>
                          </div>
                        )}
                      </div>

                      <button data-cta="demo-edit" onClick={() => setEditing((v) => !v)} style={sx('display:inline-flex;align-items:center;gap:5px;background:none;border:none;cursor:pointer;font-size:13px;font-weight:700;color:#ea580c;margin:14px 0 4px')}>
                        {svg({ width: 14, height: 14, viewBox: '0 0 24 24' }, <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z" /></>)}
                        {editing ? '編集を終了' : '内容を修正する'}
                      </button>

                      <div style={sx('display:flex;gap:10px;margin-top:8px')}>
                        <button onClick={() => go('input')} className="hover:bg-slate-50" style={sx('flex:1;padding:13px;border:1px solid #cbd5e1;border-radius:13px;background:#fff;cursor:pointer;font-size:14px;font-weight:600;color:#475569')}>戻る</button>
                        <button data-cta="demo-confirm-submit" onClick={confirmSubmit} className="hover:bg-[#c2410c] active:scale-[0.97]" style={sx('flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:13px;border:none;border-radius:13px;background:#ea580c;cursor:pointer;font-size:14px;font-weight:800;color:#fff;box-shadow:0 10px 22px -12px rgba(234,88,12,.7)')}>
                          送信する{svg({ width: 16, height: 16, viewBox: '0 0 24 24' }, <><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>)}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* SUCCESS */}
                  {screen === 'success' && (
                    <div style={sx('padding:22px 18px 26px;text-align:center;animation:apFade .35s ease both')}>
                      <div style={sx('display:inline-flex;align-items:center;justify-content:center;width:74px;height:74px;border-radius:50%;background:#d1fae5;margin-bottom:18px;animation:apBounce 1.6s ease-in-out infinite')}>
                        {svg({ width: 38, height: 38, viewBox: '0 0 24 24', stroke: '#059669' }, <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>)}
                      </div>
                      <h3 style={sx('margin:0 0 8px;font-size:23px;font-weight:800;color:#0f172a')}>送信完了</h3>
                      <p style={sx('margin:0 0 22px;font-size:14px;color:#64748b;line-height:1.7')}>貴重な報告ありがとうございます。<br />AIが内容を匿名化し、安全活動に役立てます。</p>

                      <div style={sx('background:linear-gradient(135deg,#2563eb,#4338ca);border-radius:16px;padding:16px;margin-bottom:16px;text-align:left;box-shadow:0 14px 28px -16px rgba(37,99,235,.7)')}>
                        <div style={sx('display:flex;gap:9px;margin-bottom:12px')}>
                          <span style={sx('font-size:22px;line-height:1')}>🔍</span>
                          <div>
                            <h4 style={sx('margin:0 0 3px;font-size:15px;font-weight:800;color:#fff')}>この報告をさらに深掘り分析</h4>
                            <p style={sx('margin:0;font-size:11.5px;color:#c7d2fe;line-height:1.6')}>人 / 機械 / 材料 / 方法 の <strong style={sx('color:#fff')}>4観点それぞれで 5段なぜなぜ</strong> + 即時/恒久対策を AI が即時生成。無料・登録不要。</p>
                          </div>
                        </div>
                        <button data-cta="try-why-demo" onClick={onTryWhyDemo} className="hover:bg-[#eef2ff] w-full" style={sx('display:flex;align-items:center;justify-content:center;gap:6px;background:#fff;color:#4338ca;font-weight:800;font-size:13px;padding:12px;border:none;border-radius:11px;cursor:pointer')}>なぜなぜ深掘り分析を試す →</button>
                      </div>

                      <div style={sx('background:linear-gradient(120deg,#f97316,#eab308);border-radius:16px;padding:16px;margin-bottom:16px;text-align:left;box-shadow:0 14px 28px -16px rgba(249,115,22,.7)')}>
                        <span style={sx('display:inline-block;font-size:10px;font-weight:800;letter-spacing:.06em;color:#fff;background:rgba(255,255,255,.22);border-radius:999px;padding:3px 10px;margin-bottom:9px')}>CONGRATS</span>
                        <h4 style={sx('margin:0 0 6px;font-size:16px;font-weight:800;color:#fff')}>30秒の体験、いかがでしたか？</h4>
                        <p style={sx('margin:0 0 13px;font-size:12px;color:rgba(255,255,255,.95);line-height:1.65')}>これがあなたの現場で動きます。無料プランから登録すれば、QRコード3個・AI分析10回/月までずっと無料。</p>
                        <button data-cta="signup" onClick={onSignup} className="hover:bg-[#fff7ed] w-full" style={sx('display:flex;align-items:center;justify-content:center;background:#fff;color:#ea580c;font-weight:800;font-size:13px;padding:12px;border:none;border-radius:11px;cursor:pointer')}>無料で始める（カード不要）→</button>
                      </div>

                      <div style={sx('background:#ecfdf5;border:1px solid #a7f3d0;border-radius:14px;padding:14px;margin-bottom:16px;text-align:left')}>
                        <div style={sx('display:flex;gap:8px')}>
                          {svg({ width: 17, height: 17, viewBox: '0 0 24 24', stroke: '#059669', style: sx('flex:0 0 auto;margin-top:1px') }, <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>)}
                          <div>
                            <p style={sx('margin:0 0 4px;font-size:13px;font-weight:800;color:#065f46')}>本番では「対応経過」も見えます</p>
                            <p style={sx('margin:0;font-size:11.5px;color:#047857;line-height:1.65')}>投稿後に専用URLが発行され、管理者の対応状況（未対応→対応中→対応済み）と実施内容を匿名のまま確認できます。「自分の声がどう活かされたか」がわかる仕組みです。</p>
                          </div>
                        </div>
                      </div>

                      <div style={sx('background:#f8fafc;border:1px solid #e2e8f0;border-radius:13px;padding:13px 15px;margin-bottom:14px;text-align:left')}>
                        <p style={sx('margin:0 0 4px;font-size:11px;color:#94a3b8')}>今月のあなたのチーム</p>
                        <div style={sx('display:flex;justify-content:space-between;align-items:flex-end')}>
                          <span style={sx('font-size:14px;font-weight:700;color:#0f172a')}>今月の報告</span>
                          <span style={sx('font-size:21px;font-weight:800;color:#ea580c')}>1<span style={sx('font-size:12px;color:#16a34a;margin-left:5px')}>(+1)</span></span>
                        </div>
                      </div>

                      <div style={sx('background:#eff6ff;border:1px solid #dbeafe;border-radius:13px;padding:12px 14px;margin-bottom:22px;text-align:left')}>
                        <p style={sx('margin:0;font-size:12.5px;color:#1e40af;line-height:1.6')}>数日後、あなたの投稿は教育用ケースとしてチームに共有されます。</p>
                      </div>

                      <div style={sx('display:flex;flex-direction:column;gap:10px')}>
                        <button data-cta="demo-continue" onClick={() => { resetForm(); go('input'); }} className="hover:bg-[#c2410c] active:scale-[0.98]" style={sx('width:100%;padding:14px;border:none;border-radius:13px;background:#ea580c;cursor:pointer;font-size:15px;font-weight:800;color:#fff')}>続けて投稿する</button>
                        <button data-cta="demo-home" onClick={() => go('home')} className="hover:bg-slate-50" style={sx('width:100%;padding:14px;border:1px solid #cbd5e1;border-radius:13px;background:#fff;cursor:pointer;font-size:14px;font-weight:600;color:#475569')}>ホームに戻る</button>
                      </div>
                    </div>
                  )}

                </div>

                {/* home indicator */}
                <div data-deskonly style={sx('flex:0 0 auto;display:flex;justify-content:center;padding:8px 0 10px')}>
                  <span style={sx('width:120px;height:4px;border-radius:999px;background:#cbd5e1')} />
                </div>
              </div>
            </div>

            <p data-deskonly style={sx('text-align:center;margin:2px 0 0;font-size:13px;color:#64748b;line-height:1.6')}>Powered by 安全ポスト＋ ・ 安全第一・整理整頓</p>
          </div>

          {/* right spacer keeps the phone centered + callout gutter */}
          <div data-deskonly style={sx('flex:0 0 210px;align-self:stretch')} />
        </div>
      </div>
    </div>
  );
};
